// Re-export new permission system
export {
  RESOURCES,
  CRUD_ACTIONS,
  SPECIAL_ACTIONS,
  RESOURCE_ACTIONS,
  RESOURCE_LABELS,
  CRUD_ACTION_LABELS,
  SPECIAL_ACTION_LABELS,
  EMPTY_PERMISSIONS,
  WILDCARD_PERMISSIONS,
  checkResourcePermission,
  checkSpecialAction,
  isWildcard,
} from "./permissions";
export type {
  Resource,
  CrudAction,
  SpecialAction,
  StructuredPermissions,
} from "./permissions";

// ============================================
// FEATURE CATALOG
// ============================================

/**
 * The three states a feature can be in for a tenant (KLUB-119 / #324).
 *
 *  - `on`          — live. The only state in which `tier_config` is consulted.
 *  - `coming_soon` — the icon stays put and the screen explains that the
 *                    feature has not opened yet. This is how a club launches:
 *                    staff loads the content while members see the notice.
 *  - `hidden`      — gone from navigation entirely.
 *
 * Persisted in `feature_flags.visibility`, which replaced the old `enabled`
 * boolean. A missing flag resolves to `hidden` (fail-closed).
 */
export const FEATURE_VISIBILITIES = ['on', 'coming_soon', 'hidden'] as const;

export type FeatureVisibility = (typeof FEATURE_VISIBILITIES)[number];

export function isFeatureVisibility(value: unknown): value is FeatureVisibility {
  return typeof value === 'string' && (FEATURE_VISIBILITIES as readonly string[]).includes(value);
}

/**
 * The catalog is DECLARED here, not discovered from the database.
 *
 * Why: `feature_flags` used to carry 14 rows for TheLatvian and only 6 of them
 * had a reader in the app. A platform screen listing all 14 would hand the
 * operator 8 switches that switch nothing — they turn off "Feed de Novidades"
 * and the app does not change. A switch that does not interrupt is worse than
 * an absent one, because it lies. Only features with a real gate live here; a
 * feature that grows a gate later joins the catalog together with that gate.
 *
 * `surface` is what the platform UI reads to know whether "hide the icon" is
 * even an offer worth making:
 *  - `tab`       — has an icon in the tab bar; `hidden` removes it.
 *  - `route`     — reachable by navigation or deep link, no icon of its own.
 *  - `component` — embedded inside another screen.
 *
 * For `route` and `component`, `hidden` and `coming_soon` differ only in what
 * someone arriving by deep link is told.
 *
 * `comingSoonTitle` / `comingSoonMessage` are the fallbacks when the tenant
 * leaves `params.coming_soon_title` / `params.coming_soon_message` empty — an
 * unconfigured tenant must never show a blank notice. `unavailableTitle` /
 * `unavailableMessage` are NOT tenant-editable: `hidden` is reached only by
 * deep link into a feature the club never opened, and there is no promise to
 * word there.
 *
 * These defaults are deliberately NOT vocabulary-aware — `@klubora/shared` has
 * no tenant context to resolve `vocabulary.challenge.plural` against. A club
 * that renames the noun overrides the copy in the platform, which is the same
 * lever that exists for every other wording here.
 */
export const FEATURE_CATALOG = [
  {
    key: 'activities',
    label: 'Atividades',
    surface: 'tab',
    comingSoonTitle: 'Atividades em breve',
    comingSoonMessage: 'A agenda ainda está sendo montada. Em breve você verá tudo o que vai acontecer por aqui.',
    unavailableTitle: 'Atividades indisponíveis',
  },
  {
    key: 'benefits',
    label: 'Benefícios',
    surface: 'tab',
    comingSoonTitle: 'Benefícios em breve',
    comingSoonMessage: 'Estamos fechando as primeiras parcerias. Em breve seus benefícios aparecem aqui.',
    unavailableTitle: 'Benefícios indisponíveis',
  },
  {
    key: 'community',
    label: 'Comunidade',
    surface: 'tab',
    comingSoonTitle: 'Comunidade em breve',
    comingSoonMessage: 'A comunidade abre em breve. Enquanto isso, seu perfil já está sendo preparado.',
    unavailableTitle: 'Comunidade indisponível',
  },
  {
    key: 'challenges',
    label: 'Desafios',
    surface: 'tab',
    comingSoonTitle: 'Desafios em breve',
    comingSoonMessage: 'Os desafios ainda não começaram. Em breve você poderá participar e acumular conquistas.',
    unavailableTitle: 'Desafios indisponíveis',
  },
  {
    key: 'opportunities',
    label: 'Mural de Oportunidades',
    surface: 'route',
    comingSoonTitle: 'Mural em breve',
    comingSoonMessage: 'O mural abre em breve, para você oferecer e encontrar oportunidades entre os membros.',
    unavailableTitle: 'Mural indisponível',
  },
  {
    key: 'referrals',
    label: 'Programa de Indicação',
    surface: 'route',
    comingSoonTitle: 'Programa de indicação em breve',
    comingSoonMessage: 'O programa de indicação está sendo preparado. Em breve você poderá convidar e ser recompensado.',
    unavailableTitle: 'Programa de indicação indisponível',
  },
  {
    key: 'checkin',
    label: 'Check-in',
    surface: 'component',
    comingSoonTitle: 'Check-in em breve',
    comingSoonMessage: 'O check-in pelo app ainda não está liberado. Por enquanto, procure a recepção.',
    unavailableTitle: 'Check-in indisponível',
  },
] as const;

export type FeatureCatalogEntry = (typeof FEATURE_CATALOG)[number];

export type FeatureFlagKey = FeatureCatalogEntry['key'];

export type FeatureSurface = FeatureCatalogEntry['surface'];

/**
 * Flat key list, derived so it can never drift from the catalog. Kept as a
 * separate export because the tier editors (`Tiers.tsx`, `TierDetail.tsx`)
 * iterate keys, not entries.
 */
export const FEATURE_FLAG_KEYS = FEATURE_CATALOG.map((f) => f.key) as readonly FeatureFlagKey[];

/**
 * Keys that used to live in `tiers.config` and still show up on plan cards.
 * They are not catalog features. A leftover `false` would hide a tab the day
 * one of these names re-enters the catalog — KLUB-173.
 */
export const DEAD_TIER_CONFIG_KEYS = ["chat", "experiences", "matchmaking"] as const;

export type DeadTierConfigKey = (typeof DEAD_TIER_CONFIG_KEYS)[number];

export function isDeadTierConfigKey(key: string): boolean {
  return (DEAD_TIER_CONFIG_KEYS as readonly string[]).includes(key);
}

export const PLAN_CUT_UNAVAILABLE_MESSAGE =
  "Seu plano não inclui esta funcionalidade.";

export const CLUB_HIDDEN_UNAVAILABLE_MESSAGE =
  "Esta funcionalidade não está ativa no seu clube.";

export function featureCatalogEntry(key: string): FeatureCatalogEntry | undefined {
  return FEATURE_CATALOG.find((f) => f.key === key);
}

/**
 * The keys the platform writes into `feature_flags.params`. Only these are
 * tenant-editable; anything else in `params` is ignored by the app.
 */
export const COMING_SOON_TITLE_PARAM = 'coming_soon_title';
export const COMING_SOON_MESSAGE_PARAM = 'coming_soon_message';
export const COMING_SOON_IMAGE_PARAM = 'coming_soon_image_url';
export const COMING_SOON_IMAGE_BUCKET = 'feature-coming-soon';
export const COMING_SOON_IMAGE_URL_MAX = 2048;

/**
 * Longest tenant-written copy the platform accepts, enforced on write.
 *
 * Mirrored in `supabase/functions/_shared/feature-catalog.ts` — the edge
 * function is what actually rejects, and Deno cannot import this file. The
 * parity test covers these limits alongside the catalog: if they drift,
 * the platform UI lets an operator save copy the server then refuses.
 */
export const COMING_SOON_TITLE_MAX = 60;
export const COMING_SOON_MESSAGE_MAX = 280;

export interface FeatureNoticeCopy {
  title: string;
  message: string;
}

function trimmedString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The title + description a member reads when a feature is not `on`.
 *
 * `coming_soon` resolves tenant override → catalog default. `hidden` never
 * consults `params`: the club writes a launch promise, not an apology for a
 * screen it chose not to open, and letting the promise leak into the deep-link
 * dead end would announce a launch that is not coming.
 *
 * The last-resort branch only fires for a key outside the catalog, which the
 * platform UI cannot produce — it exists so a stale deep link still renders a
 * sentence instead of an empty screen.
 */
export type FeatureNoticeReason = "plan_cut";

export function featureNoticeCopy(
  key: string,
  visibility: FeatureVisibility,
  params?: Record<string, unknown> | null,
  reason?: FeatureNoticeReason,
): FeatureNoticeCopy {
  const entry = featureCatalogEntry(key);

  if (visibility === 'coming_soon') {
    return {
      title: trimmedString(params?.[COMING_SOON_TITLE_PARAM])
        ?? entry?.comingSoonTitle
        ?? 'Em breve',
      message: trimmedString(params?.[COMING_SOON_MESSAGE_PARAM])
        ?? entry?.comingSoonMessage
        ?? 'Esta funcionalidade ainda não está disponível no seu clube.',
    };
  }

  return {
    title: entry?.unavailableTitle ?? 'Indisponível',
    message: reason === "plan_cut"
      ? PLAN_CUT_UNAVAILABLE_MESSAGE
      : CLUB_HIDDEN_UNAVAILABLE_MESSAGE,
  };
}

/**
 * Background image the member sees on the coming-soon poster. `hidden` (and
 * `on`) never consult this param — same leak-prevention as the launch copy.
 */
export function comingSoonImageUrl(
  visibility: FeatureVisibility,
  params?: Record<string, unknown> | null,
): string | null {
  if (visibility !== 'coming_soon') return null;
  return trimmedString(params?.[COMING_SOON_IMAGE_PARAM]);
}

const TENANT_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The write path only accepts an HTTPS object in this tenant's coming-soon
 * bucket. Anything else (arbitrary CDN, another tenant's folder, a data URL)
 * is refused so the phone never loads an operator-supplied remote we do not
 * host.
 */
export function isComingSoonImageUrl(value: string, tenantId: string): boolean {
  if (value.length === 0 || value.length > COMING_SOON_IMAGE_URL_MAX) return false;
  if (!TENANT_UUID.test(tenantId)) return false;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return false;
    const prefix = `/storage/v1/object/public/${COMING_SOON_IMAGE_BUCKET}/${tenantId}/`;
    if (!url.pathname.startsWith(prefix)) return false;
    const rest = url.pathname.slice(prefix.length);
    return rest.length > 0 && !rest.includes('/') && !rest.includes('..');
  } catch {
    return false;
  }
}

// ============================================
// OPPORTUNITY CATEGORIES
// ============================================

export const OPPORTUNITY_CATEGORIES = [
  'oferta',
  'demanda',
  'parceria',
  'investimento',
  'mentoria',
  'outro',
] as const;

export type OpportunityCategory = (typeof OPPORTUNITY_CATEGORIES)[number];

// ============================================
// SPACE CONSTANTS
// ============================================

export const BAHIA_MARINA_SPACE_NAME = 'Latvian Bahia Marina';

export const PRICING_MODELS = ['free', 'paid'] as const;

// ============================================
// BLOCKABLE FEATURES (dunning)
// ============================================

export const BLOCKABLE_FEATURES = [
  'qr_code',
  'event_rsvp',
  'space_booking',
  'experience_registration',
  'invites',
  'feed_post',
] as const;

export type BlockableFeature = typeof BLOCKABLE_FEATURES[number];

// ============================================
// FINANCIAL BLOCK (KLUB-80 / #273)
// ============================================
//
// The five doors that share the ("checkin","checkin") permission refuse with
// {error, code, block_reason}. One code for all of them, with the reason as the
// discriminating field — same shape validate-benefit-redemption already uses for
// KIND_MISMATCH + detected_kind.
//
// Mirror of supabase/functions/_shared/financial-gate-errors.ts. Deno functions
// cannot import this workspace package, so the values are kept in lockstep by
// hand and the lockstep is asserted by
// tests/shared/financial-block-reasons-parity.test.ts — a reason added on one
// side and not the other would reach the scanner with no translation.

/** {error, code} value when a door refuses on financial grounds. */
export const FINANCIAL_BLOCK = 'FINANCIAL_BLOCK';

/**
 * Explicit block on the profile. Outranks the subscription — but no longer
 * outranks a PURCHASE: since KLUB-90 (#287) a person who bought the specific
 * thing gets in despite `dunning`/`blocked`, because the club charged them for
 * it. `dispute` still refuses everything: the chargeback may be that purchase.
 * `missing_payment_method` (KLUB-147) is the same family as `dispute`: hard,
 * outside dunning, not waived by a purchase or by the tenant feature list.
 *
 * The severity list is what this file mirrors; the carve-out itself lives in
 * `refusalFor` (supabase/functions/_shared/financial-gate-errors.ts), which the
 * app never runs. Stated here anyway because this comment is what an app-side
 * reader finds first, and it used to say "refuses whatever the origin is".
 */
export const HARD_BLOCK_REASONS = ['dunning', 'dispute', 'blocked', 'missing_payment_method'] as const;

/** Subscription state. Withdraws only the right to come in AS A MEMBER. */
export const SOFT_BLOCK_REASONS = [
  'canceled',
  'past_due',
  'unpaid',
  'paused',
  'incomplete',
] as const;

export const FINANCIAL_BLOCK_REASONS = [
  ...HARD_BLOCK_REASONS,
  ...SOFT_BLOCK_REASONS,
] as const;

export type HardBlockReason = typeof HARD_BLOCK_REASONS[number];
export type SoftBlockReason = typeof SOFT_BLOCK_REASONS[number];
export type FinancialBlockReason = typeof FINANCIAL_BLOCK_REASONS[number];

// ============================================
// ACTIVITY ACCESS TERMS (KLUB-8 / #169)
// ============================================
//
// Error contract shared by the DB enforcement triggers
// (supabase/migrations/2026072110_activity_terms_003_triggers.sql), the edge
// functions and both apps. Edge fns return { error, code } with the string
// codes; direct table writes surface the custom SQLSTATEs via supabase-js
// error.code.

/** {error, code} value when an RSVP path requires a terms acceptance that
 *  does not exist yet (R5/R6/R12/R13). */
export const TERMS_ACCEPTANCE_REQUIRED = 'TERMS_ACCEPTANCE_REQUIRED';

/** {error, code} value when the presence lock (R2) rejects adding/removing
 *  terms on an activity that already has RSVPs. */
export const TERMS_LOCKED = 'TERMS_LOCKED';

/** SQLSTATE raised by the rsvps gate trigger. */
export const TERMS_ACCEPTANCE_REQUIRED_SQLSTATE = 'KLTA1';

/** SQLSTATE raised by the activities presence-lock trigger. */
export const TERMS_LOCKED_SQLSTATE = 'KLTA2';

/** {error, code} value when an RSVP/invite-accept/check-in path requires the
 *  caller to complete registration first: the activity has
 *  requires_registration=true and the profile does not hold the five legal
 *  data points (photo, RG, valid CPF, phone, address). Mirrored in
 *  supabase/functions/_shared/registration-errors.ts.
 *
 *  #197 (KLUB-13) note: the condition is the five FIELDS, evaluated by
 *  public._profile_registration_complete(), not profiles.registration_complete
 *  — that boolean is now derived FROM the fields, so it is an output of the
 *  rule rather than an input to it. */
export const REGISTRATION_REQUIRED = 'REGISTRATION_REQUIRED';

/** {error, code} value when the guest policy of a SPACE refuses the invite:
 *  the host's tier carries no `permite_convidado` row for that space, or the
 *  host is a prospect (D9 — a blocked member is evaluated as one), or the
 *  host's own access is not in order. Returned as 403 by both
 *  create-referral-invite (issue time) and accept-referral-invite (accept
 *  time). Mirrored in supabase/functions/_shared/space-guest-policy.ts.
 *
 *  KLUB-120 (#326) note: the accept screen MUST branch on this before its
 *  `wrong_tenant` fallback — a bare 403 there reads as "you knocked on the
 *  wrong club's door", which sends the guest to doubt the link instead of
 *  talking to whoever invited them.
 *
 *  KLUB-252 note: create-activity-payment also returns it as 403 when a
 *  presence lot carries guests the activity's audience rules do not allow
 *  (same fail-closed rule as create-referral-invite). The activity confirm
 *  screen maps it to its own "não aceita convidados" alert, never to the
 *  generic retry. */
export const GUEST_NOT_PERMITTED = 'GUEST_NOT_PERMITTED';

/** {error, code, invitee_name} value when the caller is the HOST of the invite
 *  they are trying to accept (KLUB-276). Returned as 422 by
 *  accept-referral-invite before anything is written; `error` already carries
 *  the sentence to show ("Este convite é para {nome}. Envie o link para ele.").
 *  No origin is exempt: a contractor's own seat is written already accepted
 *  and never reaches accept. Mirrored in
 *  supabase/functions/_shared/invite-errors.ts.
 *
 *  The resolver never sends this code: for the host's own pending invite it
 *  answers no_pending_reason "accepted_or_closed" (terminal for every shipped
 *  boot drain) plus `own_invite: {invitee_name, message}`. */
export const OWN_INVITE = 'OWN_INVITE';

/** {error, code} value when a host names their OWN e-mail as a guest at
 *  creation (KLUB-276 U7, R9). Sent by create-referral-invite and the space
 *  booking paths (per invitee), create-activity-payment (422) and
 *  manage-guest-invite. `error` carries the sentence (owner decision D14):
 *  "Esse é o seu e-mail — aqui entram só os seus convidados." Mirrored in
 *  supabase/functions/_shared/invite-errors.ts. */
export const SELF_INVITE = 'SELF_INVITE';

/** {error, code} value when the host's tier MAY bring guests to the space but
 *  has already spent its ceiling in the window of the visit date. Returned as
 *  409. Mirrored in supabase/functions/_shared/space-guest-policy.ts.
 *
 *  KLUB-120 (#326) note: the accept screen MUST branch on this before its
 *  generic 409 tail, which reads any other 409 as "race / already accepted —
 *  you're in" and routes to the tabs. Falling through would tell a guest whose
 *  invite was REFUSED that they have access. */
export const GUEST_QUOTA_EXCEEDED = 'GUEST_QUOTA_EXCEEDED';

/** {error, code} value when a presence lot names a guest who already holds a
 *  PENDING invite from the same host (KLUB-246). Returned as 409 by
 *  create-activity-payment, mapped from the 23505 of
 *  `referral_invites_pending_unique_email_idx`. That index ignores the target,
 *  so a pending invite to ANOTHER activity or benefit refuses too. Mirrored in
 *  supabase/functions/_shared/space-guest-policy.ts. */
export const GUEST_ALREADY_INVITED = 'GUEST_ALREADY_INVITED';

/** {error, code} value when the host's previous payment for this activity is
 *  still being processed, or another request of his is creating one right now
 *  (KLUB-260). Returned as 409 by create-activity-payment; nothing was charged
 *  or cancelled. Mirrored in supabase/functions/_shared/space-guest-policy.ts. */
export const PAYMENT_IN_PROGRESS = 'PAYMENT_IN_PROGRESS';

/** {error, code, details: { guests_dropped }} value when the host's previous
 *  attempt on this activity is already paid (KLUB-207 literal, a constant since
 *  KLUB-260). Returned as 409 by create-activity-payment; `guests_dropped` is
 *  true when this request named guests the paid attempt did not. Mirrored in
 *  supabase/functions/_shared/space-guest-policy.ts. */
export const ACTIVITY_ALREADY_PAID = 'already_paid';

/** {error, code} value when the host already holds a seat on this activity
 *  through an invite another member sponsored (KLUB-260): an open
 *  `sponsored_invite` hold carrying his user id collides with the payment hold.
 *  Returned as 409 by create-activity-payment before any charge; that hold is
 *  never adopted or released. Mirrored in
 *  supabase/functions/_shared/space-guest-policy.ts. */
export const SEAT_HELD_BY_INVITE = 'SEAT_HELD_BY_INVITE';

/** Space companion ceiling at invite create (KLUB-216). Same denominator as
 *  attach_booking_guest: active companions + pending invites on that booking
 *  + this batch must be <= capacity - 1. */
export const SPACE_CAPACITY_FULL = 'SPACE_CAPACITY_FULL';

/** SQLSTATE raised by the rsvps registration gate trigger (#197 / KLUB-13).
 *  Sits alongside KLTA1 (terms) and KLPA1 (payment); the trigger name places
 *  it between them in the alphabetical BEFORE-trigger order, so a row failing
 *  both payment and registration surfaces KLPA1 first. */
export const REGISTRATION_REQUIRED_SQLSTATE = 'KLRG1';

/** SQLSTATE raised by the rsvps payment gate trigger (rsvps_payment_gate_trg)
 *  when a row is written as `confirmed` on a priced activity with no payment
 *  behind it. Mirrored in supabase/functions/_shared/payment-errors.ts.
 *
 *  KLUB-46 (#230) note: this gate exempts callers whose auth.role() is neither
 *  `authenticated` nor `anon`, so it does NOT fire on the service-role paths
 *  (invite accept, staff writes) — unlike its registration sibling, which has
 *  no such exemption. The constant exists so those paths can map it anyway:
 *  the cost is one branch, and it removes the dependency on an exemption a
 *  future migration could drop silently. */
export const PAYMENT_REQUIRED_SQLSTATE = 'KLPA1';

/** {error, code} value when the account holder tries to EMPTY one of the five
 *  legal data points. They can be changed, never cleared (#197 R18). */
export const REGISTRATION_LOCKED = 'REGISTRATION_LOCKED';

/** SQLSTATE raised by the profiles emptying guard (#197 / KLUB-13, U12).
 *  Second in the KLRG family, mirroring how the terms gate pairs KLTA1
 *  (acceptance required) with KLTA2 (presence lock). Note this one is raised
 *  only for `authenticated` writers: service_role must stay free to clear the
 *  columns, because clearing them is exactly what the LGPD anonymization
 *  routines do — for 12 profiles it is the only mechanism available. */
export const REGISTRATION_LOCKED_SQLSTATE = 'KLRG2';

/** {error, code} value when the PAID path refuses because the activity has no
 *  free seat (KLUB-50 / #235, origin R10/AE5). Mirrored in
 *  supabase/functions/_shared/capacity-errors.ts.
 *
 *  This code exists because "full" is the one refusal on the charge path that
 *  retrying can never fix. Without it the client falls through to its generic
 *  "tente novamente em alguns segundos", which is advice that cannot work — the
 *  member would tap forever. The screen must instead say the activity filled up
 *  and offer the queue.
 *
 *  There is deliberately NO sibling SQLSTATE. The capacity rule in the database
 *  DEMOTES rather than raises (a refusal would leave the feed reminder card with
 *  no path to offer), so the only surface that can refuse outright is this one:
 *  the paid path, which must refuse before a charge exists. */
export const ACTIVITY_FULL = 'ACTIVITY_FULL';

/** {error, code} value when a path refuses because the activity was cancelled
 *  (KLUB-46 / #230, R5/R14). Mirrored in
 *  supabase/functions/_shared/activity-state-errors.ts.
 *
 *  Unlike ACTIVITY_FULL there is no alternative to offer — "full" has the
 *  queue, "cancelled" has nothing — so the client must not turn this into a
 *  retry or a call to action.
 *
 *  The row stays `visibility='published'` on purpose (the member agenda reads it
 *  through a non-inner embed), so every surface that decides exposure or sending
 *  still has to filter `cancelled_at` by name. That part has not changed. */
export const ACTIVITY_CANCELLED = 'ACTIVITY_CANCELLED';

/** The default-vocabulary sentence for a cancelled-activity refusal. Since
 *  KLUB-283 the server builds its own from the club's activity term
 *  (`activityCancelledMessage` in supabase/functions/_shared/activity-state-errors.ts)
 *  and the app shows `activityWasCancelled(vocabulary)`; this constant is no
 *  longer the server's mirror.
 *
 *  It exists on this side because the code alone was never enough: six server
 *  paths refuse with ACTIVITY_CANCELLED — write-activities, create-referral-invite,
 *  preview-referral-invite, share-activity, refund-activity-payment and
 *  validate-activity-checkin — and until KLUB-46 (#230) not one client read it.
 *  Inviting, previewing, sharing, refunding and checking in on a cancelled
 *  activity all refused correctly and all surfaced as a generic failure.
 *
 *  It deliberately offers no alternative: "full" has the queue, "cancelled" has
 *  nothing, so the client must not turn this into a retry or a call to action. */
export const ACTIVITY_CANCELLED_MESSAGE = 'Esta atividade foi cancelada.';

/** SQLSTATE raised by the rsvps cancelled-activity gate trigger
 *  (`rsvps_activity_not_cancelled_trg`, KLUB-46 / #230). Mirrored in
 *  supabase/functions/_shared/rsvp-blocked-reason.ts.
 *
 *  This constant corrects a claim that used to sit above ACTIVITY_CANCELLED:
 *  "there is deliberately NO sibling SQLSTATE — cancellation is not enforced by
 *  a trigger". True when U14 wrote it, false about ninety minutes later, when
 *  migration 20260728130100 added the gate. Nobody went back, and the sentence
 *  then did real damage: it is the reason four client sites translate KLTA1 /
 *  KLRG1 / KLPA1 and none of them translate this one.
 *
 *  Two properties make it the one that MUST be mapped rather than the one that
 *  can wait. It exempts NOBODY — not even service_role — because the orphan it
 *  prevents is written BY a service_role edge function (accept-referral-invite).
 *  And `rsvps_activity_not_cancelled_trg` sorts first among the rsvps BEFORE
 *  triggers, so on a cancelled activity this is the only code that ever
 *  surfaces: KLTA1, KLRG1 and KLPA1 are never even evaluated. A caller that maps
 *  those three and not this one has mapped exactly the refusals that cannot
 *  fire. */
export const ACTIVITY_CANCELLED_SQLSTATE = 'KLAC1';

/** {error, code} value when the caller matches no audience rule on the activity
 *  — evaluated by the IDENTITY OF AUDIENCE, not the real one (KLUB-94 / #293).
 *
 *  It deliberately offers no alternative price. That is the whole point of the
 *  refusal: on an activity with no `prospect` row there is nothing to charge a
 *  prospect, so "pay the other price" is not an answer — 19 entities in
 *  production are member-only exactly this way. */
export const AUDIENCE_NOT_ELIGIBLE = 'AUDIENCE_NOT_ELIGIBLE';

/** SQLSTATE raised by the rsvps audience gate trigger
 *  (`rsvps_audience_gate_trg`, KLUB-94 / #293).
 *
 *  The gate reads `profiles.audience_role` / `audience_tier_id` — the generated
 *  columns that evaluate a member carrying `blocked_at` as a prospect. So this
 *  code reaches TWO different people who look nothing alike to the app: a real
 *  prospect on a club event, and a member blocked by dunning. The client copy
 *  must therefore branch on who is asking (`role`, which is untouched), never on
 *  this code alone — telling a member in arrears to "become a member" is the
 *  exact falsehood KLUB-93 removed from the dunning ladder.
 *
 *  Ordering: `rsvps_audience_gate_trg` sorts second among the rsvps BEFORE
 *  triggers, right after the cancelled-activity gate. It therefore fires BEFORE
 *  capacity (no seat is consumed by a refusal) and before KLPA1 — which, since
 *  KLUB-94 U2, would answer "no payment required" for a blocked member on a
 *  member-only activity, there being no `prospect` row to match. */
export const AUDIENCE_NOT_ELIGIBLE_SQLSTATE = 'KLAU1';

// ============================================
// SUPPORTED TIMEZONES (tenant identity)
// ============================================
//
// Curated IANA timezone allowlist for `tenants.timezone`. Single source of
// truth for BOTH the platform tenant <select> (create + edit) AND server-side
// write validation. A value outside this list must never reach the column:
// the booking-end-alerts cron resolves `(date + time) AT TIME ZONE tz` and
// silently drops EVERY alert for a tenant whose tz Postgres can't parse
// (issues #56 / #59). An allowlist is safer than a dynamic "accept whatever
// parses" check and the <select> needs a curated list anyway.
//
// To support a new region: add its IANA name here. The Deno-side mirror at
// `supabase/functions/_shared/timezones.ts` must stay in lockstep — parity is
// asserted by `tests/shared/timezones-parity.test.ts`.
export const SUPPORTED_TIMEZONES = [
  'America/Sao_Paulo',
] as const;

export type SupportedTimezone = (typeof SUPPORTED_TIMEZONES)[number];

export function isSupportedTimezone(tz: string): tz is SupportedTimezone {
  return (SUPPORTED_TIMEZONES as readonly string[]).includes(tz);
}

// ============================================
// HOME SANITY CAPS (KLUB-64 / #252)
// ============================================
//
// The two activity-carousel modes that carry no explicit count still need an
// upper bound on the read, or a tenant with a large catalogue makes the home
// fetch unbounded. Neither is tenant-configurable (R6): they are read ceilings,
// not product settings, so they live here rather than in `app.home`.

/** Ceiling for `events_mode = 'all'`. Deliberately generous — "all" means
 *  "every upcoming activity", and the cap only exists so a runaway catalogue
 *  cannot make the home query unbounded. */
export const HOME_ALL_MODE_CAP = 50;

/** Ceiling for `events_mode = 'period'`. This is the literal that used to sit
 *  bare in `useHome.ts` with no owner — the period query already bounded the
 *  time window, and this bounds the row count within it. Named here so the two
 *  ceilings are read from one place (R47). */
export const HOME_PERIOD_MODE_CAP = 20;

// ---------------------------------------------------------------------------
// Consent ledger — document slugs and gate codes (KLUB-142 / #357)
// ---------------------------------------------------------------------------

/** Slugs of the legal documents the consent ledger records decisions against.
 *  The slug alone identifies the club-wide ones (one per tenant and slug).
 *  MEMBERSHIP_CONTRACT is the only plan-scoped slug: a club may have several
 *  contracts, each covering N plans, and each plan resolves to exactly one
 *  contract through `legal_document_tiers` (KLUB-244). A contract decision is
 *  always recorded for a (contract, plan) pair.
 *
 *  These exist as a single exported symbol because the version of the image
 *  term used to have two independent owners: a local `IMAGE_AUTH_TERMS_VERSION`
 *  const in apps/mobile/app/profile-completion.tsx and a bare literal `1` in
 *  apps/mobile/hooks/usePrivacy.ts. Bumping one did not bump the other. The
 *  version itself is now resolved server-side and is no longer a client
 *  concern at all, but the slug still has to match on both sides. */
export const CONSENT_DOCUMENT_SLUGS = {
  TERMS_OF_USE: 'terms-of-use',
  PRIVACY_POLICY: 'privacy-policy',
  IMAGE_AUTHORIZATION: 'image-authorization',
  MEMBERSHIP_CONTRACT: 'membership-contract',
  PUSH_NOTIFICATIONS: 'push-notifications',
  AFFINITY_PROFILING: 'affinity-profiling',
  PAYMENT_BACKUP_CARD: 'payment-backup-card',
  PAYMENT_PROVIDER_CHOICE: 'payment-provider-choice',
  /** The holder's mandate to charge the membership recurrently (KLUB-143).
   *  Not an LGPD consent: charging someone who signed up is performance of a
   *  contract (art. 7, V), not consent (art. 7, I) — so the name deliberately
   *  says "authorization", never "consent". Calling it consent would import
   *  art. 8 §5's unrestricted revocability into an annual contract. What the
   *  ledger stores is proof of a contractual mandate, which is item #1 of
   *  Stripe's evidence list for a 13.2 / 4841 dispute.
   *
   *  Club-wide (tier_id NULL), unlike MEMBERSHIP_CONTRACT: the gesture does not
   *  change with the plan — the amount and the period that do vary live in the
   *  ledger's snapshot columns, not in the document's body. */
  RECURRING_CHARGE_AUTHORIZATION: 'recurring-charge-authorization',
} as const;

export type ConsentDocumentSlug =
  (typeof CONSENT_DOCUMENT_SLUGS)[keyof typeof CONSENT_DOCUMENT_SLUGS];

/** The two documents the first-access ConsentGate collects before the app is
 *  usable. Kept here rather than in the gate so the server-side seed, the gate
 *  and any future reader agree on what "required" means. */
export const REQUIRED_CONSENT_SLUGS: readonly ConsentDocumentSlug[] = [
  CONSENT_DOCUMENT_SLUGS.TERMS_OF_USE,
  CONSENT_DOCUMENT_SLUGS.PRIVACY_POLICY,
] as const;

/** {error, code} value when a door (QR, activity check-in, space walk-in)
 *  refuses because the member has no live image authorization. Returned as 409
 *  by supabase/functions/_shared/image-authorization-gate.ts.
 *
 *  R29 is unconditional: unlike the registration gate, this one has no
 *  "non-membership context" escape valve. The valve exists because the host
 *  answers for a guest's access; nobody answers for another person's image
 *  rights. */
export const IMAGE_AUTHORIZATION_REQUIRED = 'IMAGE_AUTHORIZATION_REQUIRED';

/** {error, code} value when a subscription path refuses because the person who
 *  will become the member has no live acceptance of the membership contract of
 *  the TARGET plan. Returned as 409 by
 *  supabase/functions/_shared/membership-contract-gate.ts.
 *
 *  KTD5 note: this gate deliberately does NOT reuse
 *  REGISTRATION_EXEMPT_SUBSCRIPTION_STATUSES. Exempting people who already hold
 *  a live subscription would break R21 for 100% of the paying base while the
 *  gate still existed, still had tests, and never refused anyone who mattered.
 *  A live subscription is not an answer to "did you accept the contract of the
 *  plan you are moving TO?". */
export const CONTRACT_ACCEPTANCE_REQUIRED = 'CONTRACT_ACCEPTANCE_REQUIRED';

/** KLUB-143 — as três recusas que o caminho do dinheiro passa a poder devolver.
 *
 *  POR QUE CADA UMA PRECISA DE NOME PRÓPRIO. `describeConfirmError` discrimina
 *  por código ANTES da situação HTTP, e tudo que chega como 409 sem código
 *  conhecido cai em "Convite indisponível" com `invitationGone: true` — que
 *  dispara refetch, reencontra o convite pendente e intacto, e prende a pessoa
 *  no laço que o KLUB-141 consertou. Não é o texto genérico que morde: é o ramo
 *  de convite cancelado, e ele MENTE a causa. Sem estes nomes, um plano com
 *  Price arquivado tiraria a pessoa do funil dizendo que o convite sumiu.
 *
 *  As três viajam no BUNDLE do app, e por isso sobem antes do servidor — ver a
 *  ordem de deploy do ticket. A cópia serve os aparelhos dessa entrega em
 *  diante; quem ainda não atualizou é protegido pela ordem, não pelo texto. */

/** O corpo chegou sem o booleano da autorização de cobrança, ou com ele falso. */
export const BILLING_AUTHORIZATION_REQUIRED = 'BILLING_AUTHORIZATION_REQUIRED';

/** O plano alvo não tem Price cunhado, ou o Price foi arquivado na conta da
 *  Stripe do clube. Espelho do literal de
 *  supabase/functions/_shared/contract-snapshot.ts — Deno não importa o pacote
 *  npm, mesma doutrina dos slugs do livro. Até o KLUB-143 esta recusa só
 *  alcançava a tela do contrato; com a autorização de cobrança resolvendo o
 *  retrato antes de cobrar, ela passa a alcançar a folha de pagamento. */
export const CONTRACT_PLAN_NOT_PRICED = 'CONTRACT_PLAN_NOT_PRICED';

/** Staff confirmando por terceiro sem mandato vigente do titular. Só o titular
 *  produz mandato: uma caixa marcada por outra pessoa criaria no livro uma
 *  linha indistinguível da que o titular produziria. */
export const BILLING_AUTHORIZATION_NOT_HELD = 'BILLING_AUTHORIZATION_NOT_HELD';

/** A prova não pôde ser gravada — perfil sem clube, documento de outro clube,
 *  falha na escrita. Existe para que isso não saia como 500 sem nome, já que
 *  sob R12 ele impede a assinatura de nascer. */
export const BILLING_AUTHORIZATION_UNRECORDABLE = 'BILLING_AUTHORIZATION_UNRECORDABLE';

/** Confirm refused because a subscription other than this invitation's own
 *  incomplete payment is still on the member. 409 without this code is read
 *  as "the invitation is gone", which refetches a still-pending invite. */
export const PREVIOUS_ADHESION_LIVE = 'PREVIOUS_ADHESION_LIVE';

/** The incomplete payment this invitation already opened can no longer be
 *  presented to the card sheet. */
export const INFLIGHT_PAYMENT_UNRESUMABLE = 'INFLIGHT_PAYMENT_UNRESUMABLE';

/** KLUB-274: the Stripe subscription behind the member's incomplete payment
 *  already ended (`incomplete_expired`/`canceled`). Nothing was charged; the
 *  member starts a new attempt instead of reopening this one. */
export const PAYMENT_ATTEMPT_ENDED = 'PAYMENT_ATTEMPT_ENDED';

/** SQLSTATE raised by the consent_ledger append-only guard (U4). */
export const CONSENT_LEDGER_APPEND_ONLY_SQLSTATE = 'KLCL1';

/** SQLSTATE raised by the profiles trigger that refuses the transition of
 *  registration_complete from false to true without a live image
 *  authorization (U13). A TRANSITION guard, not a member of the registration
 *  predicate: putting it in the predicate would make revoking the image
 *  authorization un-complete the registration, and _rsvps_enforce_registration
 *  exempts nobody — so revoking would start blocking RSVPs, which no
 *  requirement asks for. */
export const IMAGE_AUTHORIZATION_REQUIRED_SQLSTATE = 'KLRG3';

/** SQLSTATE raised by the subscriptions contract backstop (U18) when a write
 *  ENTERS a billable state with no live contract acceptance behind it. */
export const CONTRACT_ACCEPTANCE_REQUIRED_SQLSTATE = 'KLCT1';

// ---------------------------------------------------------------------------
// KLUB-244 — one membership contract covers N plans
//
// Mirrored by hand in supabase/functions/_shared/legal-errors.ts: Deno does not
// import this package (same doctrine as the consent slugs).
// ---------------------------------------------------------------------------

/** 409 from write-catalog when a plan picked for a contract belongs to another
 *  contract and the move was not confirmed. The response carries `details`: one
 *  entry per plan with its name, the source contract, live subscriptions and
 *  open invitations — what the admin's confirmation shows. */
export const TIER_ALREADY_HAS_CONTRACT = 'TIER_ALREADY_HAS_CONTRACT';
export const TIER_ALREADY_HAS_CONTRACT_SQLSTATE = 'KLCT2';

/** SQLSTATE of the mirror guard: legal_documents.tier_id of a contract must be
 *  one of its linked plans. Reaching it from the admin means a stale screen
 *  tried to write the legacy field. */
export const CONTRACT_MIRROR_NOT_LINKED_SQLSTATE = 'KLCT3';

/** 409 from write-consent when the contract (or its version) the app showed is
 *  no longer the current contract of the plan. The app reloads the text and
 *  asks for the acceptance again. */
export const CONTRACT_CHANGED = 'CONTRACT_CHANGED';
export const CONTRACT_CHANGED_SQLSTATE = 'KLCT4';

/** 400 from write-catalog when a stale admin tab sends `tier_id` on a legal
 *  document. Plans are linked to a contract only through the link operation. */
export const LEGACY_TIER_FIELD = 'LEGACY_TIER_FIELD';

/** 409 from write-catalog when two changes to the same contract or plan raced
 *  (deadlock or serialization failure). The admin reloads and tries again. */
export const CONCURRENT_CHANGE = 'CONCURRENT_CHANGE';
